import { Card, CardContent, CardHeader, CardTitle } from "@aix/ui"

import type { ReportingSeoData } from "@aix/aixinternal-shared/reporting"
import { deriveWeeklyBrief } from "@aix/aixinternal-shared/reportingBrief"

export function WeeklyBriefPanel({ data, productKey }: { data: ReportingSeoData; productKey?: string }) {
  const brief = deriveWeeklyBrief(data, productKey)
  return (
    <Card>
      <CardHeader>
        <CardTitle>What deserves attention</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {brief.findings.length ? (
          <ol className="list-decimal space-y-4 pl-5">
            {brief.findings.map((finding) => (
              <li key={finding.id}>
                <h3 className="text-sm font-semibold">
                  {finding.productName} · {finding.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {finding.observation}{" "}
                  <a className="text-primary underline" href={`#product-${finding.productKey}`}>
                    Product evidence
                  </a>
                </p>
                <p className="mt-2 text-sm">
                  <span className="font-medium">Proposed review:</span> {finding.proposal}
                </p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">
            No item crosses the supported review rules for this scope. This is not an all-clear: unconnected modules and
            limited samples remain unknown.
          </p>
        )}
        {brief.findings.length ? (
          <details className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              {brief.findings.length} proposed review actions and evidence
            </summary>
            <p className="my-3 text-sm text-muted-foreground">
              Proposals only; no owners, approvals or business targets are inferred. Nothing is executed by this report.
            </p>
            <ol className="list-decimal space-y-4 pl-5">
              {brief.findings.map((finding) => (
                <li key={finding.id} className="text-sm">
                  <h3 className="font-medium">{finding.productName}</h3>
                  <p className="my-2">{finding.proposal}</p>
                  <p className="text-muted-foreground">Success gate: {finding.successGate}</p>
                  <p className="mt-2 break-all text-xs text-muted-foreground">
                    Evidence basis: {finding.evidence.periodBasis} · {finding.evidence.start} → {finding.evidence.end}.
                    Sources: {finding.evidence.sourceKeys.join(", ") || "No configured source"}. Stored collection IDs:{" "}
                    {finding.evidence.collectionIds.join(", ") ||
                      "Operational snapshot aggregates or missing collection; see product detail"}
                    .
                  </p>
                </li>
              ))}
            </ol>
          </details>
        ) : null}
        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">Coverage matrix and brief rules</summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  <th className="p-2">Product</th>
                  <th className="p-2">Search</th>
                  <th className="p-2">Checks</th>
                  <th className="p-2">Incidents</th>
                  <th className="p-2">Deployments</th>
                  <th className="p-2">Business outcomes</th>
                </tr>
              </thead>
              <tbody>
                {brief.coverage.map((row) => (
                  <tr key={row.productKey} className="border-t border-border">
                    <th className="p-2">{row.productName}</th>
                    {(["seo", "checks", "incidents", "deployments", "business"] as const).map((module) => (
                      <td key={module} className="p-2">
                        {row[module].state}
                        <p className="text-xs text-muted-foreground">{row[module].detail}</p>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {brief.thresholdNote} Up to three items, ranked by production check exceptions, evidence gaps, other
            operational exceptions, search declines and search growth. Magnitude breaks ties within a tier; the first
            pass spreads attention across products. {brief.candidateCount} candidates in this scope. Business outcomes
            are unconnected. Rule version {brief.version}; interpretations are derived from stored inputs, not an
            approved decision ledger.
          </p>
        </details>
      </CardContent>
    </Card>
  )
}
